import {
  Box,
  Button,
  Group,
  NumberInput,
  Paper,
  Table,
  Text,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import type { TFunction } from "i18next";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { distributeTarget, type SegmentTargetsPayload } from "@msc/shared";

import { zeigePlan } from "../../lib/planAnzeige.js";
import { hatAenderungen, type ZielWerte } from "../../lib/zielAenderung.js";
import { wachstumProzent } from "../../lib/wachstum.js";

import {
  usePlanningBasis,
  useSaveSegmentTargets,
  useSegmentTargets,
  useZielrunde,
} from "../../hooks/useKpi.js";

import { BegruendungModal } from "./BegruendungModal.js";
import { ZielrundeKopf } from "./ZielrundeKopf.js";

const fmtEur = (v: number | null | undefined) =>
  v == null ? "–" : `${Math.round(v).toLocaleString("de-DE")} €`;

/**
 * Zielpflege eines Bereichs (Tab „Ziele" im Bereichs-Cockpit): Jahresziele
 * mit €⇄%-Kopplung (Basis Vorjahr), Bottom-up-Referenz aus den
 * Beauftragungs-Zielen, Verteil-Helfer (gleichmäßig/Arbeitstage/Saisonprofil
 * gewichtet gemischt) und handjustierbares Monats-Grid.
 */

const MONTH_LABELS = [
  "Jan", "Feb", "Mär", "Apr", "Mai", "Jun",
  "Jul", "Aug", "Sep", "Okt", "Nov", "Dez",
];

/**
 * „Plan: 250.000 €" in gedämpfter Schrift — mit der Begründung als Tooltip,
 * wo eine vorliegt. Wird nur gerendert, wenn `zeigePlan` zutrifft (siehe
 * `lib/planAnzeige.ts` — die Runde muss eingefroren sein, sonst gibt es
 * keinen Plan, egal was der Wertevergleich sagt).
 */
function PlanHinweis({
  t,
  zielrundeStatus,
  current,
  plan,
  reason,
}: {
  t: TFunction;
  zielrundeStatus: "offen" | "eingefroren" | undefined;
  current: number | null | undefined;
  plan: number | null | undefined;
  reason: string | null | undefined;
}) {
  if (!zeigePlan(zielrundeStatus, plan, current)) return null;
  return (
    <Text
      size="xs"
      c="dimmed"
      title={
        reason
          ? t("pages.kpi.zielrunde.planBegruendungTitle", { begruendung: reason })
          : undefined
      }
    >
      {t("pages.kpi.zielrunde.plan", { betrag: fmtEur(plan) })}
    </Text>
  );
}

export function TargetsPanel({
  segmentId,
  year,
  nurLesend = false,
}: {
  segmentId: string;
  year: number;
  /**
   * Nur anzeigen, nicht bearbeiten — für Service Leads.
   *
   * Bewusst eine **eigene Ansicht** statt gesperrter Eingabefelder: Ein
   * ausgegrautes Eingabefeld sieht aus wie etwas, das man gleich ausfüllen
   * darf, und weckt genau den falschen Anschein (Rückmeldung Hendrik,
   * 2026-08-20). Die Ziele bleiben ihm sichtbar — sie sind der Bezugspunkt
   * seiner eigenen Zahlen —, nur eben als Zahlen.
   */
  nurLesend?: boolean;
}) {
  if (nurLesend) {
    return <ZieleNurLesen segmentId={segmentId} year={year} />;
  }
  return <ZielePflegen segmentId={segmentId} year={year} />;
}

function ZieleNurLesen({
  segmentId,
  year,
}: {
  segmentId: string;
  year: number;
}) {
  const { t } = useTranslation();
  const { data: basis } = usePlanningBasis(segmentId, year);
  const { data: saved } = useSegmentTargets(segmentId, year);
  const { data: zielrunde } = useZielrunde(segmentId, year);

  const eur = (v: number | null | undefined) =>
    v == null ? "–" : `${Math.round(v).toLocaleString("de-DE")} €`;
  const pct = (v: number | null | undefined) =>
    v == null ? "–" : `${v.toLocaleString("de-DE")} %`;

  return (
    <>
      <ZielrundeKopf segmentId={segmentId} year={year} darfEinfrieren={false} />
      <Paper p="md" withBorder>
        <Text fw={600} mb="xs">
          {t("pages.settings.targets.title", { year })}
        </Text>
        <Text size="xs" c="dimmed" mb="md">
          {t("pages.settings.targets.nurLesendHinweis")}
        </Text>
        <Group gap="xl" mb="md">
          <Box>
            <Text size="xs" c="dimmed">
              {t("pages.settings.targets.annualRevenue")}
            </Text>
            <Text fw={600}>{eur(saved?.revenueTargetEur)}</Text>
            <PlanHinweis
              t={t}
              zielrundeStatus={zielrunde?.status}
              current={saved?.revenueTargetEur}
              plan={saved?.planRevenueTargetEur}
              reason={saved?.planChangeReason}
            />
          </Box>
          <Box>
            <Text size="xs" c="dimmed">
              {t("pages.settings.targets.growth")}
            </Text>
            <Text fw={600}>{pct(saved?.growthTargetPct)}</Text>
          </Box>
          <Box>
            <Text size="xs" c="dimmed">
              {t("pages.settings.targets.margin")}
            </Text>
            <Text fw={600}>{pct(saved?.contributionMarginTargetPct)}</Text>
          </Box>
        </Group>
        <Table withColumnBorders>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>{t("pages.settings.targets.colMonth")}</Table.Th>
              <Table.Th>{t("pages.settings.targets.colWorkingDays")}</Table.Th>
              <Table.Th ta="right">
                {t("pages.settings.targets.colRevenue")}
              </Table.Th>
              <Table.Th ta="right">
                {t("pages.settings.targets.colHours")}
              </Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {(basis?.months ?? []).map((m, i) => (
              <Table.Tr key={m.yearMonth}>
                <Table.Td>{MONTH_LABELS[i]}</Table.Td>
                <Table.Td>{m.workingDays}</Table.Td>
                <Table.Td ta="right">
                  {eur(saved?.months[i]?.revenueTargetEur)}
                  <PlanHinweis
                    t={t}
                    zielrundeStatus={zielrunde?.status}
                    current={saved?.months[i]?.revenueTargetEur}
                    plan={saved?.months[i]?.planRevenueTargetEur}
                    reason={saved?.months[i]?.planChangeReason}
                  />
                </Table.Td>
                <Table.Td ta="right">
                  {saved?.months[i]?.billedHoursTarget != null
                    ? `${Math.round(saved.months[i]!.billedHoursTarget!).toLocaleString("de-DE")} h`
                    : `${Math.round(m.computedBilledHoursTarget).toLocaleString("de-DE")} h`}
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Paper>
    </>
  );
}

function ZielePflegen({
  segmentId,
  year,
}: {
  segmentId: string;
  year: number;
}) {
  const { t } = useTranslation();
  const { data: basis } = usePlanningBasis(segmentId, year);
  const { data: saved } = useSegmentTargets(segmentId, year);
  const { data: zielrunde } = useZielrunde(segmentId, year);
  const save = useSaveSegmentTargets();

  const [annualRevenue, setAnnualRevenue] = useState<number | string>("");
  const [growthPct, setGrowthPct] = useState<number | string>("");
  const [marginPct, setMarginPct] = useState<number | string>("");
  const [sickDays, setSickDays] = useState<number | string>("");
  const [monthRevenue, setMonthRevenue] = useState<(number | string)[]>(
    Array.from({ length: 12 }, () => ""),
  );
  const [monthHours, setMonthHours] = useState<(number | string)[]>(
    Array.from({ length: 12 }, () => ""),
  );
  const [wUniform, setWUniform] = useState<number | string>(0);
  const [wWorkingDays, setWWorkingDays] = useState<number | string>(50);
  const [wSeason, setWSeason] = useState<number | string>(50);
  const [begruendungOpen, setBegruendungOpen] = useState(false);
  const [begruendung, setBegruendung] = useState("");

  // Eingefroren sperrt das formlose Speichern — dieselbe Regel wie bei den
  // Beauftragungen (`ZielAendernModal`), nur eine Ebene höher: Der Server
  // verlangt ab dann `begruendung` (400 `BEGRUENDUNG_FEHLT`), die Oberfläche
  // bietet dafür „Speichern" den Dialog statt einer Sackgasse an.
  const eingefroren = zielrunde?.status === "eingefroren";

  /**
   * Beim Laden wird die Umsatzsteigerung NEU BERECHNET, nicht aus der
   * Datenbank übernommen.
   *
   * Sie ist keine eigene Größe, sondern eine Sicht auf zwei andere:
   * Zielumsatz gegen Vorjahresumsatz. Gespeichert wurde sie trotzdem — und
   * damit driftete sie ab, sobald sich der **Vorjahresumsatz** änderte. Genau
   * das ist am 2026-08-12 passiert: Seit die Abgänge im Bestand bleiben, zählt
   * der Vorjahresumsatz auch die Kunden mit, deren letztes Projekt im Vorjahr
   * endete. Er stieg, das gespeicherte Prozent blieb stehen — und die Maske
   * behauptete 17 %, wo 10,2 % richtig waren (aufgefallen 2026-08-20).
   *
   * Die gespeicherte Spalte bleibt unangetastet; sie wird nur nicht mehr
   * angezeigt. Ein abgeleiteter Wert darf keine zweite Wahrheit haben.
   */
  useEffect(() => {
    if (!saved) return;
    setAnnualRevenue(saved.revenueTargetEur ?? "");
    setGrowthPct(
      wachstumProzent(saved.revenueTargetEur, basis?.previousYearRevenueEur) ??
        "",
    );
    setMarginPct(saved.contributionMarginTargetPct ?? "");
    setSickDays(saved.sickDaysTarget ?? "");
    setMonthRevenue(saved.months.map((m) => m.revenueTargetEur ?? ""));
    setMonthHours(saved.months.map((m) => m.billedHoursTarget ?? ""));
  }, [saved, basis]);

  const applyDistribution = () => {
    if (!basis || annualRevenue === "") return;
    const values = distributeTarget(
      Number(annualRevenue),
      {
        uniform: Number(wUniform || 0),
        workingDays: Number(wWorkingDays || 0),
        season: Number(wSeason || 0),
      },
      basis.months.map((m) => m.workingDays),
      basis.months.map((m) => m.seasonShare),
    );
    setMonthRevenue(values);
  };

  const doSave = (begruendungValue?: string) => {
    if (!basis) return;
    const payload: SegmentTargetsPayload = {
      revenueTargetEur: annualRevenue === "" ? null : Number(annualRevenue),
      growthTargetPct: growthPct === "" ? null : Number(growthPct),
      contributionMarginTargetPct: marginPct === "" ? null : Number(marginPct),
      sickDaysTarget: sickDays === "" ? null : Number(sickDays),
      months: basis.months.map((m, i) => ({
        yearMonth: m.yearMonth,
        revenueTargetEur:
          monthRevenue[i] === "" ? null : Number(monthRevenue[i]),
        billedHoursTarget: monthHours[i] === "" ? null : Number(monthHours[i]),
      })),
      ...(begruendungValue ? { begruendung: begruendungValue } : {}),
    };
    save.mutate(
      { segmentId, year, payload },
      {
        onSuccess: () => {
          setBegruendungOpen(false);
          setBegruendung("");
        },
      },
    );
  };

  /**
   * Gespeicherter Stand in derselben Form wie der Entwurf — Grundlage für
   * `hatAenderungen` (s. `lib/zielAenderung.ts`). Das Wachstum wird genauso
   * ABGELEITET wie beim Laden des Entwurfs (s. Kommentar oben bei
   * `useEffect`), nie aus `saved.growthTargetPct` übernommen: Sonst würde ein
   * längst veralteter, nur noch in der Spalte stehender DB-Wert eine
   * Änderung vortäuschen, die keine ist.
   */
  const gespeicherterStand: ZielWerte | null = useMemo(() => {
    if (!saved) return null;
    return {
      jahresziel: saved.revenueTargetEur,
      wachstum: wachstumProzent(
        saved.revenueTargetEur,
        basis?.previousYearRevenueEur,
      ),
      deckungsbeitrag: saved.contributionMarginTargetPct,
      krankheitstage: saved.sickDaysTarget,
      monatsUmsatz: saved.months.map((m) => m.revenueTargetEur),
      monatsStunden: saved.months.map((m) => m.billedHoursTarget),
    };
  }, [saved, basis]);

  // Ob der Entwurf gegenüber dem gespeicherten Stand überhaupt etwas
  // geändert hat. Ohne gespeicherten Stand (noch nicht geladen) gilt
  // „keine Änderung" — derselbe vorsichtige Default wie bei `zeigePlan`.
  const hatEntwurfAenderungen = useMemo(() => {
    if (!gespeicherterStand) return false;
    const entwurf: ZielWerte = {
      jahresziel: annualRevenue,
      wachstum: growthPct,
      deckungsbeitrag: marginPct,
      krankheitstage: sickDays,
      monatsUmsatz: monthRevenue,
      monatsStunden: monthHours,
    };
    return hatAenderungen(gespeicherterStand, entwurf);
  }, [
    gespeicherterStand,
    annualRevenue,
    growthPct,
    marginPct,
    sickDays,
    monthRevenue,
    monthHours,
  ]);

  // Offen: Speichern speichert direkt, wie bisher — dort schadet ein
  // Speichern ohne Änderung nicht. Eingefroren: „Speichern" öffnet erst den
  // Begründungsdialog, die eigentliche Übernahme läuft über dessen `onSave`
  // (s. u.) — außer es gibt gegenüber dem gespeicherten Stand gar nichts zu
  // speichern, dann bleibt der Dialog zu und es geht keine Anfrage an den
  // Server (Review-Befund: sonst landet eine bedeutungslose Begründung in
  // der Akte).
  const handleSaveClick = () => {
    if (eingefroren) {
      if (!hatEntwurfAenderungen) {
        notifications.show({
          message: t("pages.settings.targets.keineAenderungToast"),
          color: "yellow",
          autoClose: 4000,
          withBorder: true,
        });
        return;
      }
      setBegruendungOpen(true);
      return;
    }
    doSave();
  };

  // Ob sich die Monatsverteilung gegenüber dem beim Einfrieren festgehaltenen
  // Plan ändert — dieselbe Regel wie `PlanHinweis` je Monat (`zeigePlan`),
  // nur vorausschauend auf den Entwurf statt auf den gespeicherten Stand: Der
  // Dialog zeigt, was das bevorstehende Speichern bewirken WIRD.
  const monatsverteilungGeaendert = useMemo(() => {
    if (!saved) return false;
    return saved.months.some((m, i) => {
      const draft = monthRevenue[i] === "" ? null : Number(monthRevenue[i]);
      return zeigePlan(zielrunde?.status, m.planRevenueTargetEur, draft);
    });
  }, [saved, monthRevenue, zielrunde?.status]);

  const monthSum = monthRevenue.reduce<number>(
    (a, v) => a + (v === "" ? 0 : Number(v)),
    0,
  );

  return (
    <>
      <ZielrundeKopf segmentId={segmentId} year={year} darfEinfrieren />
      <Paper p="md" withBorder mb="md">
        <Text fw={600} size="sm" mb="xs">
          {t("pages.settings.targets.annualTitle")}
        </Text>
        {/* Referenzwerte: Vorjahr (Basis der €⇄%-Kopplung) und Bottom-up-Summe
            der Beauftragungs-Ziele — Top-down vs. Bottom-up. */}
        <Group gap="xl" mb="sm">
          <Box>
            <Text size="xs" c="dimmed">
              {t("pages.settings.targets.prevYearRevenue")}
            </Text>
            <Text fw={600}>
              {basis
                ? `${Math.round(basis.previousYearRevenueEur).toLocaleString("de-DE")} €`
                : "–"}
            </Text>
          </Box>
          <Box>
            <Text size="xs" c="dimmed">
              {t("pages.settings.targets.engagementSum", {
                count: basis?.engagementTargetsCount ?? 0,
                total: basis?.engagementCount ?? 0,
              })}
            </Text>
            <Group gap="xs">
              <Text fw={600}>
                {basis
                  ? `${Math.round(basis.engagementTargetsSumEur).toLocaleString("de-DE")} €`
                  : "–"}
              </Text>
              <Button
                size="compact-xs"
                variant="subtle"
                disabled={!basis || basis.engagementTargetsSumEur <= 0}
                onClick={() => {
                  if (!basis) return;
                  const v = Math.round(basis.engagementTargetsSumEur);
                  setAnnualRevenue(v);
                  if (basis.previousYearRevenueEur > 0) {
                    setGrowthPct(
                      Math.round(
                        (v / basis.previousYearRevenueEur - 1) * 1000,
                      ) / 10,
                    );
                  }
                }}
              >
                {t("pages.settings.targets.adoptEngagementSum")}
              </Button>
            </Group>
          </Box>
          {annualRevenue !== "" && basis && basis.engagementTargetsSumEur > 0 && (
            <Box>
              <Text size="xs" c="dimmed">
                {t("pages.settings.targets.deltaBottomUp")}
              </Text>
              <Text
                fw={600}
                c={
                  Number(annualRevenue) > basis.engagementTargetsSumEur
                    ? "orange"
                    : "green"
                }
              >
                {`${Math.round(Number(annualRevenue) - basis.engagementTargetsSumEur).toLocaleString("de-DE")} €`}
              </Text>
            </Box>
          )}
        </Group>
        <Group gap="sm">
          <Box>
            <NumberInput
              label={t("pages.settings.targets.annualRevenue")}
              value={annualRevenue}
              onChange={(v) => {
                setAnnualRevenue(v);
                if (basis && basis.previousYearRevenueEur > 0 && v !== "") {
                  setGrowthPct(
                    Math.round(
                      (Number(v) / basis.previousYearRevenueEur - 1) * 1000,
                    ) / 10,
                  );
                }
              }}
              min={0}
              step={10000}
              suffix=" €"
              style={{ width: 180 }}
            />
            <PlanHinweis
              t={t}
              zielrundeStatus={zielrunde?.status}
              current={saved?.revenueTargetEur}
              plan={saved?.planRevenueTargetEur}
              reason={saved?.planChangeReason}
            />
          </Box>
          <NumberInput
            label={t("pages.settings.targets.growth")}
            value={growthPct}
            onChange={(v) => {
              setGrowthPct(v);
              if (basis && basis.previousYearRevenueEur > 0 && v !== "") {
                setAnnualRevenue(
                  Math.round(
                    basis.previousYearRevenueEur * (1 + Number(v) / 100),
                  ),
                );
              }
            }}
            min={-100}
            step={0.5}
            suffix=" %"
            disabled={!basis || basis.previousYearRevenueEur <= 0}
            style={{ width: 140 }}
          />
          <NumberInput
            label={t("pages.settings.targets.margin")}
            value={marginPct}
            onChange={setMarginPct}
            min={0}
            max={100}
            step={1}
            suffix=" %"
            style={{ width: 150 }}
          />
          <NumberInput
            label={t("pages.settings.targets.sickDays")}
            value={sickDays}
            onChange={setSickDays}
            min={0}
            step={0.5}
            style={{ width: 150 }}
          />
        </Group>
      </Paper>

      <Paper p="md" withBorder mb="md">
        <Text fw={600} size="sm" mb="xs">
          {t("pages.settings.targets.distributionTitle")}
        </Text>
        <Text c="dimmed" size="xs" mb="xs">
          {t("pages.settings.targets.distributionHint")}
        </Text>
        <Group gap="sm" align="flex-end">
          <NumberInput
            label={t("pages.settings.targets.weightUniform")}
            value={wUniform}
            onChange={setWUniform}
            min={0}
            max={100}
            style={{ width: 130 }}
          />
          <NumberInput
            label={t("pages.settings.targets.weightWorkingDays")}
            value={wWorkingDays}
            onChange={setWWorkingDays}
            min={0}
            max={100}
            style={{ width: 130 }}
          />
          <NumberInput
            label={t("pages.settings.targets.weightSeason")}
            value={wSeason}
            onChange={setWSeason}
            min={0}
            max={100}
            style={{ width: 130 }}
          />
          <Button
            variant="light"
            disabled={annualRevenue === "" || !basis}
            onClick={applyDistribution}
          >
            {t("pages.settings.targets.applyDistribution")}
          </Button>
        </Group>
      </Paper>

      <Paper p="md" withBorder>
        <Group justify="space-between" mb="xs">
          <Text fw={600} size="sm">
            {t("pages.settings.targets.monthsTitle")}
          </Text>
          <Text
            size="sm"
            c={
              annualRevenue !== "" && monthSum !== Number(annualRevenue)
                ? "orange"
                : "dimmed"
            }
          >
            {t("pages.settings.targets.monthSum", {
              sum: monthSum.toLocaleString("de-DE"),
            })}
          </Text>
        </Group>
        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>{t("pages.settings.targets.colMonth")}</Table.Th>
              <Table.Th>{t("pages.settings.targets.colWorkingDays")}</Table.Th>
              <Table.Th>{t("pages.settings.targets.colSeason")}</Table.Th>
              <Table.Th>{t("pages.settings.targets.colRevenue")}</Table.Th>
              <Table.Th>{t("pages.settings.targets.colHours")}</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {(basis?.months ?? []).map((m, i) => (
              <Table.Tr key={m.yearMonth}>
                <Table.Td>{MONTH_LABELS[i]}</Table.Td>
                <Table.Td>{m.workingDays}</Table.Td>
                <Table.Td>{(m.seasonShare * 100).toFixed(1)} %</Table.Td>
                <Table.Td>
                  <NumberInput
                    size="xs"
                    value={monthRevenue[i]}
                    onChange={(v) =>
                      setMonthRevenue((prev) => {
                        const next = [...prev];
                        next[i] = v;
                        return next;
                      })
                    }
                    min={0}
                    step={1000}
                    suffix=" €"
                    style={{ width: 150 }}
                  />
                  <PlanHinweis
                    t={t}
                    zielrundeStatus={zielrunde?.status}
                    current={saved?.months[i]?.revenueTargetEur}
                    plan={saved?.months[i]?.planRevenueTargetEur}
                    reason={saved?.months[i]?.planChangeReason}
                  />
                </Table.Td>
                <Table.Td>
                  <NumberInput
                    size="xs"
                    value={monthHours[i]}
                    onChange={(v) =>
                      setMonthHours((prev) => {
                        const next = [...prev];
                        next[i] = v;
                        return next;
                      })
                    }
                    min={0}
                    step={10}
                    suffix=" h"
                    placeholder={t("pages.settings.targets.computedHours", {
                      hours: Math.round(m.computedBilledHoursTarget),
                    })}
                    style={{ width: 160 }}
                  />
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
        <Group justify="flex-end" mt="sm">
          <Button loading={save.isPending} onClick={handleSaveClick}>
            {t("common.save")}
          </Button>
        </Group>
      </Paper>

      <BegruendungModal
        opened={begruendungOpen}
        onClose={() => setBegruendungOpen(false)}
        title={t("pages.kpi.zielrunde.aenderungTitelBereich")}
        hinweis={t("pages.kpi.zielrunde.aenderungHinweis")}
        begruendung={begruendung}
        onBegruendungChange={setBegruendung}
        onSave={() => doSave(begruendung)}
        saving={save.isPending}
        errorMessage={
          save.isError
            ? save.error instanceof Error
              ? save.error.message
              : t("common.error")
            : null
        }
      >
        <Text size="sm" mb={4}>
          {t("pages.kpi.zielrunde.plan", {
            betrag: fmtEur(saved?.planRevenueTargetEur),
          })}
        </Text>
        <Text size="xs" c="dimmed" mb={monatsverteilungGeaendert ? 4 : "md"}>
          {t("pages.kpi.zielrunde.wirdGesetzt", {
            betrag: fmtEur(annualRevenue === "" ? null : Number(annualRevenue)),
          })}
        </Text>
        {monatsverteilungGeaendert && (
          <Text size="xs" c="dimmed" mb="md">
            {t("pages.kpi.zielrunde.monatsverteilungGeaendert")}
          </Text>
        )}
      </BegruendungModal>
    </>
  );
}
